import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Approval, HarnessCard, canonicalTool, summarizeApproval } from "./harness";

const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const day = (iso: string) => new Date(iso).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
const pct = (x: number) => `${Math.round(x * 100)}%`;
const date = (v?: string) => (v ? new Date(v).toLocaleDateString() : "");

function CalendarCard({ data }: { data: { appointments: any[] } }) {
  const { t } = useTranslation();
  const appts = [...(data.appointments ?? [])].sort((a, b) => a.start_at.localeCompare(b.start_at));
  let lastDay = "";
  return (
    <div className="hcard">
      <div className="hcard__title">📅 {t("assistant.cards.calendar")}</div>
      {appts.length === 0 && <div className="hcard__muted">{t("assistant.cards.noAppointments")}</div>}
      {appts.map((a) => {
        const d = day(a.start_at);
        const showDay = d !== lastDay;
        lastDay = d;
        return (
          <React.Fragment key={a.appointment_id}>
            {showDay && <div className="hcard__label">{d}</div>}
            <div className={`hcard__row ${a.status === "cancelled" ? "is-cancelled" : ""}`}>
              <span className="hcard__time">{time(a.start_at)}–{time(a.end_at)}</span>
              <span>{a.patient_name ?? t("assistant.cards.unknownPatient")}</span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function SimilarCard({ kind, data }: { kind: "similar_sessions" | "similar_moments"; data: any }) {
  const { t } = useTranslation();
  const items: { key: string; when: string; score: number; text: string; label?: string }[] =
    kind === "similar_sessions"
      ? (data.similar ?? []).map((x: any) => ({ key: x.id, when: date(x.session_date), score: x.similarity, text: x.summary ?? "" }))
      : (data.moments ?? []).map((x: any, i: number) => ({
          key: `${x.past_session_id}-${x.past_moment_idx ?? i}`, when: date(x.past_session_date),
          score: x.similarity, text: x.past_moment_excerpt ?? "", label: x.past_moment_label }));
  return (
    <div className="hcard">
      <div className="hcard__title">🔁 {t(`assistant.cards.${kind}`)}</div>
      {items.length === 0 && <div className="hcard__muted">{t("assistant.cards.noMatches")}</div>}
      {items.map((it) => (
        <div key={it.key} className="hcard__item">
          <div className="hcard__label">{it.when} · {pct(it.score)}{it.label ? ` · ${it.label}` : ""}</div>
          <div className="hcard__text">{it.text}</div>
        </div>
      ))}
    </div>
  );
}

function ChairAudioCard({ data }: { data: { clips: { id: string; label: string; voice: string; url: string }[] } }) {
  const { t } = useTranslation();
  return (
    <div className="hcard">
      <div className="hcard__title">🪑 {t("assistant.cards.chair_audio")}</div>
      {(data.clips ?? []).map((c) => (
        <div key={c.id} className="hcard__item">
          <div className="hcard__label">{c.label} · {c.voice}</div>
          <audio controls preload="none" src={c.url} style={{ width: "100%" }} />
        </div>
      ))}
      <div className="hcard__muted">{t("assistant.cards.audioExpires")}</div>
    </div>
  );
}

export function CardView({ card }: { card: HarnessCard }) {
  switch (card.kind) {
    case "calendar":
      return <CalendarCard data={card.data} />;
    case "similar_sessions":
    case "similar_moments":
      return <SimilarCard kind={card.kind} data={card.data} />;
    case "chair_audio":
      return <ChairAudioCard data={card.data} />;
  }
}

/** Claude Code-style permission prompt above the composer. One decision
 * answers every pending request (the server requires all of them). */
export function ApprovalPanel({ approvals, busy, onAnswer }: {
  approvals: Approval[];
  busy: boolean;
  onAnswer: (approve: boolean) => void;
}) {
  const { t } = useTranslation();
  const summaries = useMemo(() => approvals.map((a) => {
    try {
      return { ok: true as const, a, s: summarizeApproval(a) };
    } catch (e) {
      console.error("approval arguments unreadable", a, e);
      return { ok: false as const, a };
    }
  }), [approvals]);
  if (approvals.length === 0) return null;
  return (
    <div className="happroval" role="dialog" aria-label={t("assistant.approval.title")}>
      <div className="happroval__title">{t("assistant.approval.title")}</div>
      {summaries.map((x) => {
        const tool = canonicalTool(x.a.tool);
        return (
          <div key={x.a.approval_request_id} className="happroval__item">
            <div className="happroval__tool">{t(`assistant.approval.tools.${tool}`, { defaultValue: tool })}</div>
            {!x.ok && <div className="happroval__error">{t("assistant.approval.unreadable")}</div>}
            {x.ok && x.s.kind === "chair" && (
              <>
                {x.s.lines.map((l, i) => (
                  <div key={i} className="happroval__line">
                    <div className="hcard__label">{l.label} · {l.voice}</div>
                    <div>“{l.text}”</div>
                  </div>
                ))}
                <div className="hcard__muted">{t("assistant.approval.chairCost")}</div>
              </>
            )}
            {x.ok && x.s.kind === "generic" && <pre className="happroval__raw">{x.s.raw}</pre>}
          </div>
        );
      })}
      <div className="happroval__actions">
        <button type="button" className="happroval__deny" disabled={busy} onClick={() => onAnswer(false)}>
          {t("assistant.approval.deny")}
        </button>
        <button type="button" className="happroval__allow" disabled={busy} onClick={() => onAnswer(true)}>
          {t("assistant.approval.allow")}
        </button>
      </div>
    </div>
  );
}

export const harnessStyles = `
  .hcard { border: 1px solid var(--color-border); background: var(--color-subsurface); border-radius: var(--radius-md);
    padding: 10px 12px; margin-top: 8px; display: flex; flex-direction: column; gap: 6px; font-size: 13px; }
  .hcard__title { font-weight: 600; color: var(--color-title); }
  .hcard__label { font-size: 11px; font-weight: 700; letter-spacing: var(--tracking-wide); text-transform: uppercase; color: var(--color-muted); }
  .hcard__row { display: flex; gap: 10px; }
  .hcard__row.is-cancelled { text-decoration: line-through; color: var(--color-muted); }
  .hcard__time { min-width: 92px; color: var(--color-muted); font-variant-numeric: tabular-nums; }
  .hcard__item { display: flex; flex-direction: column; gap: 2px; }
  .hcard__text { color: var(--color-ink); display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
  .hcard__muted { color: var(--color-muted); font-size: 12px; }
  .happroval { border: 1px solid var(--color-accent); background: var(--color-accent-ghost); border-radius: var(--radius-md);
    padding: 12px; margin: 8px 12px; display: flex; flex-direction: column; gap: 10px; }
  .happroval__title { font-weight: 700; color: var(--color-title); }
  .happroval__tool { font-weight: 600; }
  .happroval__item { display: flex; flex-direction: column; gap: 6px; }
  .happroval__line { padding-left: 8px; border-left: 2px solid var(--color-border-strong); }
  .happroval__raw { font-size: 12px; background: var(--color-surface); padding: 8px; border-radius: var(--radius-sm); overflow: auto; max-height: 200px; margin: 0; }
  .happroval__error { color: var(--color-danger); }
  .happroval__actions { display: flex; gap: 8px; justify-content: flex-end; }
  .happroval__actions button { padding: 7px 16px; border-radius: 999px; font-size: 13px; font-weight: 600; cursor: pointer; }
  .happroval__deny { background: transparent; border: 1px solid var(--color-border-strong); color: var(--color-ink); }
  .happroval__allow { background: var(--color-brand); border: 1px solid var(--color-brand); color: #fff; }
  .happroval__actions button:disabled { opacity: 0.5; cursor: default; }
`;
